import * as z from "zod";
import { storageKey } from "@/site";

export const DEVICE_READERS_KEY = storageKey("deviceReaders.v1");
/** The earlier single-reader key, read until the first write replaces it. */
export const LAST_READER_KEY = storageKey("lastReader.v1");

const deviceReaderSchema = z.object({
  readerId: z.uuid(),
  lastSeenAt: z.number().finite(),
  /** Whether an account holds the reader; absent until an answer says. */
  linked: z.boolean().optional(),
});

const deviceReadersSchema = z.array(deviceReaderSchema);

export type DeviceReader = z.infer<typeof deviceReaderSchema>;

type Store = Pick<Storage, "getItem" | "setItem" | "removeItem">;

const browserStorage = (): Store | null => {
  try {
    return localStorage;
  } catch {
    return null;
  }
};

function legacyDeviceReaders(storage: Store | null): DeviceReader[] {
  const readerId = z.uuid().safeParse(storage?.getItem(LAST_READER_KEY));
  return readerId.success ? [{ readerId: readerId.data, lastSeenAt: 0 }] : [];
}

/**
 * Every reader this device has read as, most recent first. A sign-in that
 * ends without a reader replaces the device's reader_session, and this is
 * how the device gets back to each of them, with whatever their outboxes
 * still hold. No cap: a reader dropped here loses the only session that
 * could still send its rows. A picker of the device's readers reads this
 * list and shows what it will of it.
 */
export function deviceReaders(
  storage: Store | null = browserStorage(),
): DeviceReader[] {
  const raw = storage?.getItem(DEVICE_READERS_KEY);
  if (!raw) return legacyDeviceReaders(storage);
  try {
    const parsed = deviceReadersSchema.safeParse(JSON.parse(raw));
    if (parsed.success) return parsed.data;
  } catch {
    // Unparseable: no readers, and cleared below.
  }
  storage?.removeItem(DEVICE_READERS_KEY);
  return [];
}

function write(readers: DeviceReader[], storage: Store | null): void {
  if (readers.length === 0) storage?.removeItem(DEVICE_READERS_KEY);
  else storage?.setItem(DEVICE_READERS_KEY, JSON.stringify(readers));
  storage?.removeItem(LAST_READER_KEY);
}

/** Makes the reader the device's most recent, keeping what it knew of it. */
export function rememberDeviceReader(
  readerId: string,
  storage: Store | null = browserStorage(),
  now: number = Date.now(),
): void {
  const readers = deviceReaders(storage);
  const known = readers.find((reader) => reader.readerId === readerId);
  const others = readers.filter((reader) => reader.readerId !== readerId);
  write([{ ...known, readerId, lastSeenAt: now }, ...others], storage);
}

export function lastReader(
  storage: Store | null = browserStorage(),
): string | null {
  return deviceReaders(storage)[0]?.readerId ?? null;
}

export function isDeviceReader(
  readerId: string,
  storage: Store | null = browserStorage(),
): boolean {
  return deviceReaders(storage).some((reader) => reader.readerId === readerId);
}

export function forgetDeviceReader(
  readerId: string,
  storage: Store | null = browserStorage(),
): void {
  const readers = deviceReaders(storage);
  if (!readers.some((reader) => reader.readerId === readerId)) return;
  write(
    readers.filter((reader) => reader.readerId !== readerId),
    storage,
  );
}

/**
 * What a session answer says about whether an account holds one of the
 * device's readers. An answer that cannot say leaves the last word standing.
 */
export function noteReaderLinked(
  readerId: string,
  linked: boolean | null,
  storage: Store | null = browserStorage(),
): void {
  if (linked === null) return;
  const readers = deviceReaders(storage);
  const reader = readers.find((candidate) => candidate.readerId === readerId);
  if (reader === undefined || reader.linked === linked) return;
  write(
    readers.map((candidate) =>
      candidate.readerId === readerId ? { ...candidate, linked } : candidate,
    ),
    storage,
  );
}

/** The device's most recent reader, if a session has said an account holds it. */
export function linkedLastReader(
  storage: Store | null = browserStorage(),
): string | null {
  const latest = deviceReaders(storage)[0];
  return latest?.linked === true ? latest.readerId : null;
}
